import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getEmailLogs, PAGE_SIZES, toLimitOffset } from '../services/api';

function EmailLog() {
  const { t } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pageSize, setPageSize] = useState(50);

  const [filters, setFilters] = useState({
    type: '',
    status: '',
    recipient: '',
    subject: '',
  });

  const [page, setPage] = useState(1);

  const activeParams = useMemo(() => {
    const params = {};
    if (filters.type) params.type = filters.type;
    if (filters.status) params.status = filters.status;
    if (filters.recipient.trim()) params.recipient = filters.recipient.trim();
    if (filters.subject.trim()) params.subject = filters.subject.trim();
    Object.assign(params, toLimitOffset(page - 1, pageSize));
    return params;
  }, [filters, page, pageSize]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil((total || 0) / pageSize)), [total, pageSize]);

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getEmailLogs(activeParams);
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err.message || t('admin.email_log.errors.load_failed'));
      setLogs([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [activeParams, t]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const resetFilters = () => {
    setFilters({ type: '', status: '', recipient: '', subject: '' });
    setPage(1);
  };

  return (
    <div className="license-management">
      <h2>{t('admin.email_log.title')}</h2>

      <div className="filters">
        <label>
          {t('admin.email_log.filters.type')}
          <select
            value={filters.type}
            onChange={(e) => {
              setFilters((prev) => ({ ...prev, type: e.target.value }));
              setPage(1);
            }}
          >
            <option value="">{t('admin.email_log.filters.any')}</option>
            <option value="TEST">{t('admin.email_log.types.test')}</option>
            <option value="LICENSE">{t('admin.email_log.types.license')}</option>
            <option value="REGISTRATION_REQUESTED">{t('admin.email_log.types.registration_requested')}</option>
            <option value="REGISTRATION_APPROVED">{t('admin.email_log.types.registration_approved')}</option>
            <option value="PASSWORD_RESET_REQUESTED">{t('admin.email_log.types.password_reset_requested')}</option>
          </select>
        </label>

        <label>
          {t('admin.email_log.filters.status')}
          <select
            value={filters.status}
            onChange={(e) => {
              setFilters((prev) => ({ ...prev, status: e.target.value }));
              setPage(1);
            }}
          >
            <option value="">{t('admin.email_log.filters.any')}</option>
            <option value="OK">{t('admin.email_log.status.ok')}</option>
            <option value="ERROR">{t('admin.email_log.status.error')}</option>
            <option value="BLOCKED">{t('admin.email_log.status.blocked')}</option>
          </select>
        </label>

        <label>
          {t('admin.email_log.filters.recipient')}
          <input
            type="text"
            value={filters.recipient}
            onChange={(e) => {
              setFilters((prev) => ({ ...prev, recipient: e.target.value }));
              setPage(1);
            }}
            placeholder={t('admin.email_log.filters.recipient_placeholder')}
          />
        </label>

        <label>
          {t('admin.email_log.filters.subject')}
          <input
            type="text"
            value={filters.subject}
            onChange={(e) => {
              setFilters((prev) => ({ ...prev, subject: e.target.value }));
              setPage(1);
            }}
            placeholder={t('admin.email_log.filters.subject_placeholder')}
          />
        </label>

        <button type="button" className="btn btn-secondary" onClick={resetFilters}>
          {t('admin.email_log.filters.reset')}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading">{t('admin.email_log.loading')}</div>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>{t('admin.email_log.table.created_at')}</th>
              <th>{t('admin.email_log.table.type')}</th>
              <th>{t('admin.email_log.table.status')}</th>
              <th>{t('admin.email_log.table.recipient')}</th>
              <th>{t('admin.email_log.table.subject')}</th>
            </tr>
          </thead>
          <tbody>
            {logs.length === 0 ? (
              <tr>
                <td colSpan="5" style={{ textAlign: 'center' }}>
                  {t('admin.email_log.empty')}
                </td>
              </tr>
            ) : (
              logs.map((l) => (
                <tr key={l.id}>
                  <td style={{ whiteSpace: 'nowrap' }}>{l.created_at}</td>
                  <td>{l.type_}</td>
                  <td>{l.status}</td>
                  <td>{l.recipient_email}</td>
                  <td title={l.subject || ''}>{l.subject || '-'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}

      <div className="pagination" style={{ marginTop: '12px' }}>
        <span style={{ marginRight: '10px' }}>
          {t('admin.email_log.pagination.page_size')}{' '}
          <select
            value={pageSize}
            onChange={(e) => {
              const n = parseInt(e.target.value, 10);
              if (!PAGE_SIZES.includes(n)) return;
              setPageSize(n);
              setPage(1);
            }}
          >
            {PAGE_SIZES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </span>
        <button
          type="button"
          className="btn btn-secondary"
          disabled={page <= 1}
          onClick={() => setPage(1)}
          title={t('common.pagination_first')}
        >
          «
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          disabled={page <= 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          title={t('common.pagination_prev')}
        >
          ‹
        </button>
        <span style={{ padding: '0 10px' }}>
          {t('admin.email_log.pagination.page_of', { page, totalPages, total })}
        </span>
        <button
          type="button"
          className="btn btn-secondary"
          disabled={page >= totalPages}
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          title={t('common.pagination_next')}
        >
          ›
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          disabled={page >= totalPages}
          onClick={() => setPage(totalPages)}
          title={t('common.pagination_last')}
        >
          »
        </button>
      </div>
    </div>
  );
}

export default EmailLog;

